import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tabs,
  Typography,
  Spin,
  List,
  Radio,
  Switch,
} from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  CloudUploadOutlined,
  SettingOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  HolderOutlined,
} from '@ant-design/icons'
import MultiImageUploader from '@/components/MultiImageUploader'

import type { ColumnsType } from 'antd/es/table'
import { scriptApi, characterApi, merchantApi, merchantRoomApi } from '@/services/api'

const { Search } = Input
const { Option } = Select
const { TabPane } = Tabs
const { Text } = Typography

enum questionnaireTypeEnum {
  single = 'single', //单选
  multiple = 'multiple', //多选
  form = 'form', //表单
}

interface questionnaireModal {
  title: string
  type: questionnaireTypeEnum
  options?: string[]
}

interface ScriptStatistics {
  total_count: number,
  draft_count: number,
  pending_review_count: number,
  reviewed_count: number,
  published_count: number,
}

interface Script {
  id: number
  uuid: string
  title: string
  description: string
  playwright_id: number
  playwright_name: string
  status: 'draft' | 'pending_review' | 'approved' | 'published' | 'rejected'
  difficulty_level: string
  estimated_duration: number
  max_players: number
  created_at: string
  updated_at: string
  story_summary?: string
  world_setting?: string
  characters?: Character[],
  photos?: string[],
  questionnaire?: questionnaireModal[]
}

interface Character {
  id: number
  name: string
  role: string
  description: string
  personality: string
  background: string
  is_secret: boolean
  intro?: string
}

interface Merchant {
  id: number
  name: string
  status: string
}

interface Venue {
  id: number
  name: string
  merchant_id: number
  address?: string
}

const ScriptManagement: React.FC = () => {
  const [scripts, setScripts] = useState<Script[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingScript, setEditingScript] = useState<Script | null>(null)
  const [characters, setCharacters] = useState<Character[]>([])
  // 分页状态
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  // 统计状态
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    pending_review: 0,
    approved: 0,
    published: 0
  })

  // 下发相关状态
  const [deployModalVisible, setDeployModalVisible] = useState(false)
  const [deployingScript, setDeployingScript] = useState<Script | null>(null)
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [venues, setVenues] = useState<Venue[]>([])
  const [selectedMerchant, setSelectedMerchant] = useState<number | null>(null)
  const [selectedVenue, setSelectedVenue] = useState<number | null>(null)
  const [merchantsLoading, setMerchantsLoading] = useState(false)
  const [venuesLoading, setVenuesLoading] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [currentUserRole, setCurrentUserRole] = useState<string>('')

  const [form] = Form.useForm()
  const [photos, setPhotos] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [questionnaire, setQuestionnaire] = useState<questionnaireModal[]>([])
  const [editingQuestion, setEditingQuestion] = useState<questionnaireModal | null>(null)
  const [questionModalVisible, setQuestionModalVisible] = useState(false)
  const [tempOptions, setTempOptions] = useState<string[]>([])
  const [tempQuestionType, setTempQuestionType] = useState<'single' | 'multiple' | 'form'>('single')
  const [tempQuestionTitle, setTempQuestionTitle] = useState('')

  // 获取剧本列表
  const fetchScripts = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
        search: searchText || undefined,
        status: statusFilter || undefined,
        include_deleted: includeDeleted
      }
      const response = await scriptApi.getScripts(params)
      const items = response.data.items || []
      const total = response.data.total || 0
      setScripts(items)
      setPagination(prev => ({ ...prev, total }))
    } catch (error) {
      message.error('获取剧本列表失败')
    } finally {
      setLoading(false)
    }
  }

  // 获取角色列表
  const fetchCharacters = async (scriptId: number) => {
    try {
      const response = await characterApi.getCharacters({ script_id: scriptId })
      setCharacters(response.data.items)
    } catch (error) {
      message.error('获取角色列表失败')
    }
  }

  // 获取剧本统计数据
  const fetchScriptStatistics = async () => {
    try {
      const response = await scriptApi.getScriptStatistics()
      const data = response.data
      setStats({
        total: data.total_count || 0,
        draft: data.draft_count || 0,
        pending_review: data.pending_review_count || 0,
        approved: data.reviewed_count || 0,
        published: data.published_count || 0
      })
    } catch (error) {
      console.error('获取剧本统计数据失败:', error)
    }
  }

  useEffect(() => {
    fetchScripts()
    // 获取当前用户角色
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      setCurrentUserRole(user.role || '')
    }
  }, [searchText, statusFilter, includeDeleted, pagination.current])

  // 获取剧本统计数据
  useEffect(() => {
    fetchScriptStatistics()
  }, [])

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value)
  }

  // 处理状态筛选
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
  }

  // 处理新建/编辑
  const handleEdit = (script?: Script) => {
    setEditingScript(script || null)
    if (script) {
      form.setFieldsValue(script)
      setPhotos(script.photos || [])
      setQuestionnaire(script.questionnaire || [])
      fetchCharacters(script.id)
    } else {
      form.resetFields()
      setCharacters([])
      setPhotos([])
      setQuestionnaire([])
    }
    setModalVisible(true)
  }
  // 处理保存
  const handleSave = async (values: any) => {
    setSaving(true)
    try {
      const data = {
        ...values,
        photos: photos,
        questionnaire: questionnaire,
      }
      console.log('保存数据:', data)
      if (editingScript) {
        await scriptApi.updateScript(editingScript.id, data)
        message.success('剧本更新成功')
      } else {
        await scriptApi.createScript(data)
        message.success('剧本创建成功')
      }
      setModalVisible(false)
      fetchScripts()
    } catch (error) {
      console.error('保存失败:', error)
      message.error('保存失败')
    } finally {
      setSaving(false)
    }
  }

  // 打开新增问题弹窗
  const handleAddQuestion = () => {
    setEditingQuestion(null)
    setTempQuestionTitle('')
    setTempOptions(['', ''])
    setTempQuestionType('single')
    setQuestionModalVisible(true)
  }

  // 打开编辑问题弹窗
  const handleEditQuestion = (question: questionnaireModal) => {
    setEditingQuestion(question)
    setTempQuestionTitle(question.title)
    setTempOptions(question.options && question.options.length > 0 ? question.options : ['', ''])
    setTempQuestionType(question.type)
    setQuestionModalVisible(true)
  }

  // 删除问题
  const handleDeleteQuestion = (index: number) => {
    const newQuestionnaire = [...questionnaire]
    newQuestionnaire.splice(index, 1)
    setQuestionnaire(newQuestionnaire)
  }

  // 保存问题
  const handleSaveQuestion = () => {
    if (!tempQuestionTitle || !tempQuestionTitle.trim()) {
      message.error('请输入问题内容')
      return
    }

    if ((tempQuestionType === 'single' || tempQuestionType === 'multiple') && tempOptions.length < 2) {
      message.error('选项至少需要2项')
      return
    }

    if ((tempQuestionType === 'single' || tempQuestionType === 'multiple') && tempOptions.some(opt => !opt.trim())) {
      message.error('选项内容不能为空')
      return
    }

    const question: questionnaireModal = {
      title: tempQuestionTitle.trim(),
      type: tempQuestionType as questionnaireTypeEnum,
      options: (tempQuestionType === 'single' || tempQuestionType === 'multiple') ? tempOptions.map(opt => opt.trim()) : undefined,
    }

    if (editingQuestion) {
      const index = questionnaire.findIndex(q => q === editingQuestion)
      if (index !== -1) {
        const newQuestionnaire = [...questionnaire]
        newQuestionnaire[index] = question
        setQuestionnaire(newQuestionnaire)
      }
    } else {
      setQuestionnaire([...questionnaire, question])
    }

    setQuestionModalVisible(false)
    setEditingQuestion(null)
    setTempQuestionTitle('')
    setTempOptions([])
    setTempQuestionType('single')
  }

  // 添加选项
  const handleAddOption = () => {
    setTempOptions([...tempOptions, ''])
  }

  // 删除选项
  const handleDeleteOption = (index: number) => {
    if (tempOptions.length <= 2) {
      message.error('选项至少需要保留2项')
      return
    }
    const newOptions = [...tempOptions]
    newOptions.splice(index, 1)
    setTempOptions(newOptions)
  }

  // 更新选项
  const handleUpdateOption = (index: number, value: string) => {
    const newOptions = [...tempOptions]
    newOptions[index] = value
    setTempOptions(newOptions)
  }

  // 移动选项
  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === tempOptions.length - 1)
    ) {
      return
    }
    const newOptions = [...tempOptions]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const temp = newOptions[index]
    newOptions[index] = newOptions[targetIndex]
    newOptions[targetIndex] = temp
    setTempOptions(newOptions)
  }

  // 移动问题
  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === questionnaire.length - 1)
    ) {
      return
    }
    const newQuestionnaire = [...questionnaire]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const temp = newQuestionnaire[index]
    newQuestionnaire[index] = newQuestionnaire[targetIndex]
    newQuestionnaire[targetIndex] = temp
    setQuestionnaire(newQuestionnaire)
  }

  // 关闭问题弹窗
  const handleCloseQuestionModal = () => {
    setQuestionModalVisible(false)
    setEditingQuestion(null)
    setTempQuestionTitle('')
    setTempOptions([])
    setTempQuestionType('single')
  }

  // 处理删除
  const handleDelete = async (id: number) => {
    try {
      await scriptApi.deleteScript(id)
      message.success('删除成功')
      fetchScripts()
    } catch (error) {
      message.error('删除失败')
    }
  }

  // 处理提交审核
  const handleSubmit = async (id: number) => {
    try {
      await scriptApi.submitForReview(id)
      message.success('提交审核成功')
      fetchScripts()
    } catch (error) {
      message.error('提交审核失败')
    }
  }

  // 获取商家列表
  const fetchMerchants = async () => {
    setMerchantsLoading(true)
    try {
      const response = await merchantApi.getMerchants({ page: 1, page_size: 100, status: 'active' })
      setMerchants(response.data.items || [])
    } catch (error) {
      message.error('获取商家列表失败')
    } finally {
      setMerchantsLoading(false)
    }
  }

  // 获取场馆列表（根据选中的商家）
  const fetchVenues = async (merchantId: number) => {
    setVenuesLoading(true)
    try {
      const response = await merchantRoomApi.getVenuesByMerchant(merchantId)
      setVenues(response.data.items || [])
    } catch (error) {
      message.error('获取场馆列表失败')
    } finally {
      setVenuesLoading(false)
    }
  }

  // 打开下发弹窗
  const handleDeployClick = (script: Script) => {
    setDeployingScript(script)
    setSelectedMerchant(null)
    setSelectedVenue(null)
    setVenues([])

    // 如果是商家管理员，尝试获取该商家的场馆
    if (currentUserRole === 'merchant_admin') {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        const user = JSON.parse(userStr)
        // 尝试从 extra_metadata 获取 merchant_id
        const merchantId = user.extra_metadata?.merchant_id
        if (merchantId) {
          setSelectedMerchant(merchantId)
          fetchVenues(merchantId)
        } else {
          // 如果没有，获取所有商家让用户选择
          message.info('请选择您的商家')
          fetchMerchants()
        }
      }
    } else {
      // 其他角色，获取所有商家
      fetchMerchants()
    }

    setDeployModalVisible(true)
  }

  // 跳转到配置管理
  const handleConfigManagement = (script: Script) => {
    // 跳转到配置块管理页面，并传递 script_id 参数
    window.location.href = `/config-blocks?script_id=${script.id}`
  }

  // 处理商家选择
  const handleMerchantChange = (merchantId: number) => {
    setSelectedMerchant(merchantId)
    setSelectedVenue(null)
    if (merchantId) {
      fetchVenues(merchantId)
    } else {
      setVenues([])
    }
  }

  // 确认下发
  const handleDeployConfirm = async () => {
    if (!selectedVenue) {
      message.error('请选择要下发的场馆')
      return
    }

    setDeploying(true)
    try {
      // 调用导出 API，传递 merchant_id（从选中的场馆获取）
      await scriptApi.exportScript(deployingScript!.id, selectedMerchant || 0)
      message.success('剧本包下发成功！已上传到 OSS 并发送 MQTT 通知')
      setDeployModalVisible(false)
    } catch (error: any) {
      message.error(error.response?.data?.message || '下发失败')
    } finally {
      setDeploying(false)
    }
  }

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default'
      case 'pending_review': return 'processing'
      case 'approved': return 'success'
      case 'published': return 'purple'
      case 'rejected': return 'error'
      default: return 'default'
    }
  }

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return '草稿'
      case 'pending_review': return '待审核'
      case 'approved': return '已审核'
      case 'published': return '已发布'
      case 'rejected': return '已拒绝'
      default: return status
    }
  }

  const columns: ColumnsType<Script> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => <Text copyable>{id}</Text>
    },
    {
      title: '剧本名称',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: '编剧',
      dataIndex: 'playwright_name',
      key: 'playwright_name'
    },
    {
      title: '难度',
      dataIndex: 'difficulty_level',
      key: 'difficulty_level',
      render: (level: string) => {
        const colors = {
          'easy': 'green',
          'medium': 'orange',
          'hard': 'red'
        }
        const texts = {
          'easy': '简单',
          'medium': '中等',
          'hard': '困难'
        }
        return <Tag color={colors[level as keyof typeof colors] || 'default'}>
          {texts[level as keyof typeof texts] || level}
        </Tag>
      }
    },
    {
      title: '玩家人数',
      dataIndex: 'max_players',
      key: 'max_players',
      render: (count: number) => `${count}人`
    },
    {
      title: '预计时长',
      dataIndex: 'estimated_duration',
      key: 'estimated_duration',
      render: (duration: number) => `${duration}分钟`
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: '操作',
      dataIndex: 'action',
      fixed: 'right' as const,
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleEdit(record)}
          >
            查看
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 'draft' && (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleSubmit(record.id)}
            >
              提交
            </Button>
          )}
          <Button
            type="link"
            icon={<CloudUploadOutlined />}
            onClick={() => handleDeployClick(record)}
          >
            下发
          </Button>
          <Button
            type="link"
            icon={<SettingOutlined />}
            onClick={() => handleConfigManagement(record)}
          >
            配置管理
          </Button>
          <Popconfirm
            title="确定要删除这个剧本吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">编剧工作台</h1>
        <p className="text-gray-600">管理和创作剧本，为玩家提供沉浸式的游戏体验</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} className="mb-6">
        <Col span={4}>
          <Card>
            <Statistic
              title="总剧本数"
              value={stats.total}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="草稿"
              value={stats.draft}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="待审核"
              value={stats.pending_review}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="已审核"
              value={stats.approved}
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="已发布"
              value={stats.published}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>
      {/* 搜索和筛选 */}
      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-4">
            <Search
              placeholder="搜索剧本名称"
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <Select
              placeholder="筛选状态"
              allowClear
              style={{ width: 150 }}
              onChange={handleStatusFilter}
            >
              <Option value="">全部状态</Option>
              <Option value="draft">草稿</Option>
              <Option value="pending_review">待审核</Option>
              <Option value="approved">已审核</Option>
              <Option value="published">已发布</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                size="small"
                checked={includeDeleted}
                onChange={setIncludeDeleted}
              />
              <span className="text-sm text-gray-500">显示已删除</span>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleEdit()}
          >
            新建剧本
          </Button>
        </div>

        {/* 剧本列表 */}
        <Table
          columns={columns}
          dataSource={scripts}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 10 }))
            },
          }}
        />
      </Card>

      {/* 新建/编辑模态框 */}
      <Modal
        title={
          <span>
            {editingScript ? '编辑剧本' : '新建剧本'}
            {editingScript && (
              <Text type="secondary" className="ml-3 text-sm">
                ID: {editingScript.id}
              </Text>
            )}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={1000}
        footer={null}
        maskClosable={!saving}
        closable={!saving}
      >
        <Spin spinning={saving} tip="保存中...">
          <Tabs defaultActiveKey="basic">
            <TabPane tab="基本信息" key="basic">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
              >
                <Form.Item
                  label="剧本名称"
                  name="title"
                  rules={[{ required: true, message: '请输入剧本名称' }]}
                >
                  <Input placeholder="输入剧本名称" />
                </Form.Item>

                <Form.Item
                  label="难度等级"
                  name="difficulty_level"
                  rules={[{ required: true }]}
                >
                  <Select placeholder="选择难度等级">
                    <Option value="easy">简单</Option>
                    <Option value="medium">中等</Option>
                    <Option value="hard">困难</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label="最大玩家人数"
                  name="max_players"
                  rules={[{ required: true }]}
                >
                  <Input type="number" min={1} max={20} placeholder="最大玩家人数" />
                </Form.Item>

                <Form.Item
                  label="预计时长（分钟）"
                  name="estimated_duration"
                  rules={[{ required: true }]}
                >
                  <Input type="number" min={30} max={480} placeholder="预计游戏时长" />
                </Form.Item>

                <Form.Item
                  label="剧本描述"
                  name="description"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="描述剧本的背景故事、游戏目标等"
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                      保存
                    </Button>
                    <Button onClick={() => setModalVisible(false)} disabled={saving}>
                      取消
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab="高级设置" key="advanced">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
              >
                <Form.Item label="投放目标" name="deployment_target">
                  <Input placeholder="如：提高品牌知名度、增加用户转化等" />
                </Form.Item>

                <Form.Item label="投放计划" name="deployment_plan">
                  <Input.TextArea
                    rows={4}
                    placeholder="详细的投放时间安排和策略"
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                      保存
                    </Button>
                    <Button onClick={() => setModalVisible(false)} disabled={saving}>
                      取消
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab="世界观设置" key="world">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
              >
                <Form.Item
                  label="剧情简介"
                  name="story_summary"
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="简述剧本的剧情背景、世界观设定等（给玩家看的简介）"
                  />
                </Form.Item>

                <Form.Item
                  label="详细世界设定"
                  name="world_setting"
                >
                  <Input.TextArea
                    rows={8}
                    placeholder="详细的世界设定信息，包括派系、场景、主要角色关系等"
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                      保存
                    </Button>
                    <Button onClick={() => setModalVisible(false)} disabled={saving}>
                      取消
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <PictureOutlined className="mr-1" />
                  相册
                </span>
              }
              key="photos"
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
              >
                <div className="py-4">
                  <p className="text-gray-600 mb-4">
                    上传剧本的相关图片，用于展示剧本封面、宣传图等。图片可以拖拽排序。
                  </p>
                  <MultiImageUploader
                    value={photos}
                    onChange={setPhotos}
                  />
                  <Form.Item className="mt-4">
                    <Space>
                      <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                        保存
                      </Button>
                      <Button onClick={() => setModalVisible(false)} disabled={saving}>
                        取消
                      </Button>
                    </Space>
                  </Form.Item>
                </div>
              </Form>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <QuestionCircleOutlined className="mr-1" />
                  调查问卷
                </span>
              }
              key="questionnaire"
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
              >
                <div className="py-2">
                  <div className="mb-2">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddQuestion}
                    >
                      新增问题
                    </Button>
                  </div>

                  {questionnaire.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      暂无问卷问题，点击上方按钮添加
                    </div>
                  ) : (
                    <List
                      dataSource={questionnaire}
                      renderItem={(question, index) => (
                        <List.Item
                          key={index}
                          actions={[
                            <Button
                              key="up"
                              type="text"
                              icon={<HolderOutlined />}
                              onClick={() => handleMoveQuestion(index, 'up')}
                              disabled={index === 0}
                            />,
                            <Button
                              key="down"
                              type="text"
                              icon={<HolderOutlined style={{ transform: 'rotate(180deg)' }} />}
                              onClick={() => handleMoveQuestion(index, 'down')}
                              disabled={index === questionnaire.length - 1}
                            />,
                            <Button
                              key="edit"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => handleEditQuestion(question)}
                            />,
                            <Popconfirm
                              key="delete"
                              title="确定要删除这个问题吗？"
                              onConfirm={() => handleDeleteQuestion(index)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>,
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Space>
                                <span>{index + 1}.</span>
                                <span>{question.title}</span>
                                <Tag color={
                                  question.type === 'single' ? 'blue' :
                                    question.type === 'multiple' ? 'green' : 'orange'
                                }>
                                  {question.type === 'single' ? '单选' :
                                    question.type === 'multiple' ? '多选' : '表单'}
                                </Tag>
                              </Space>
                            }
                            description={
                              question.options && question.options.length > 0 ? (
                                <div className="mt-2">
                                  {question.options.map((option, optIndex) => (
                                    <Tag key={optIndex} className="mr-2 mb-1">
                                      {option}
                                    </Tag>
                                  ))}
                                </div>
                              ) : null
                            }
                          />
                        </List.Item>
                      )}
                    />
                  )}

                  <Form.Item className="mt-4">
                    <Space>
                      <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>
                        保存
                      </Button>
                      <Button onClick={() => setModalVisible(false)} disabled={saving}>
                        取消
                      </Button>
                    </Space>
                  </Form.Item>
                </div>
              </Form>
            </TabPane>
          </Tabs>
        </Spin>
      </Modal>

      {/* 问题编辑弹窗 */}
      <Modal
        title={editingQuestion ? '编辑问题' : '新增问题'}
        open={questionModalVisible}
        onCancel={handleCloseQuestionModal}
        footer={null}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="问题内容" required>
            <Input.TextArea
              rows={3}
              placeholder="请输入问题内容"
              value={tempQuestionTitle}
              onChange={(e) => setTempQuestionTitle(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="问题类型" required>
            <Radio.Group
              value={tempQuestionType}
              onChange={(e) => {
                const value = e.target.value
                setTempQuestionType(value)
                if (value === 'form') {
                  setTempOptions([])
                } else if (tempOptions.length === 0) {
                  setTempOptions(['', ''])
                }
              }}
            >
              <Radio value="single">单选</Radio>
              <Radio value="multiple">多选</Radio>
              <Radio value="form">表单</Radio>
            </Radio.Group>
          </Form.Item>

          {(tempQuestionType === 'single' || tempQuestionType === 'multiple') && (
            <Form.Item label="选项（至少2项）">
              <div className="space-y-2">
                {tempOptions.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder={`选项 ${index + 1}`}
                      value={option}
                      onChange={(e) => handleUpdateOption(index, e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="text"
                      icon={<HolderOutlined style={{ transform: 'rotate(90deg)' }} />}
                      onClick={() => handleMoveOption(index, 'up')}
                      disabled={index === 0}
                    />
                    <Button
                      type="text"
                      icon={<HolderOutlined style={{ transform: 'rotate(-90deg)' }} />}
                      onClick={() => handleMoveOption(index, 'down')}
                      disabled={index === tempOptions.length - 1}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteOption(index)}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddOption}
                  className="w-full mt-2"
                >
                  添加选项
                </Button>
              </div>
            </Form.Item>
          )}

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" onClick={handleSaveQuestion}>
                保存问题
              </Button>
              <Button onClick={handleCloseQuestionModal}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 下发弹窗 */}
      <Modal
        title={
          <span>
            <CloudUploadOutlined className="mr-2" />
            下发剧本到场馆
          </span>
        }
        open={deployModalVisible}
        onCancel={() => setDeployModalVisible(false)}
        onOk={handleDeployConfirm}
        confirmLoading={deploying}
        width={600}
        okText="确认下发"
        cancelText="取消"
      >
        <div className="py-4">
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <p><strong>剧本：</strong>{deployingScript?.title}</p>
            <p><strong>剧本ID：</strong>{deployingScript?.id}</p>
          </div>

          <Form layout="vertical">
            {/* 商家管理员：只显示场馆列表 */}
            {currentUserRole === 'merchant_admin' ? (
              <Form.Item label="选择场馆">
                <Select
                  placeholder="请选择要下发的场馆"
                  value={selectedVenue}
                  onChange={setSelectedVenue}
                  loading={venuesLoading}
                  showSearch
                  optionFilterProp="children"
                >
                  {venues.map((venue: any) => (
                    <Option key={venue.id} value={venue.id}>
                      [{venue.id}] {venue.name || venue.venue_name} {venue.address && `(${venue.address})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <>
                {/* 其他角色：商家 -> 场馆 二级选择 */}
                <Form.Item label="选择商家">
                  <Select
                    placeholder="请先选择商家"
                    value={selectedMerchant}
                    onChange={handleMerchantChange}
                    loading={merchantsLoading}
                    showSearch
                    optionFilterProp="children"
                  >
                    {merchants.map((merchant: any) => (
                      <Option key={merchant.id} value={merchant.id}>
                        [{merchant.id}] {merchant.name || merchant.company_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="选择场馆">
                  <Select
                    placeholder={selectedMerchant ? "请选择场馆" : "请先选择商家"}
                    value={selectedVenue}
                    onChange={setSelectedVenue}
                    loading={venuesLoading}
                    disabled={!selectedMerchant}
                    showSearch
                    optionFilterProp="children"
                  >
                    {venues.map((venue: any) => (
                      <Option key={venue.id} value={venue.id}>
                        [{venue.id}] {venue.name || venue.venue_name} {venue.address && `(${venue.address})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            {selectedVenue && (
              <div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded">
                <p className="mb-0">
                  <strong>确认信息：</strong>
                  <br />
                  剧本包将下发到选中的场馆，系统会自动：
                  <br />
                  • 导出剧本包为 JSON 运行格式
                  <br />
                  • 上传到 OSS 对象存储
                  <br />
                  • 通过 MQTT 通知场馆服务器更新
                </p>
              </div>
            )}
          </Form>
        </div>
      </Modal>
    </div>
  )
}

export default ScriptManagement
